import { create } from 'zustand';
import {
  DEFAULT_PROJECT, PMMA_COLORS, PETG_COLORS, PVC_COLORS, WOOD_COLORS,
  getColorsForMaterial,
} from '../mock/mock';

function byId(arr, id) { return arr.find((x) => x.id === id); }
function uid() { return Math.random().toString(36).slice(2, 9); }

function makeMeta(opts = {}) {
  return {
    name: opts.name ?? 'Nouveau projet',
    client: opts.client ?? '',
    installAddress: opts.installAddress ?? '',
    contactEmail: opts.contactEmail ?? '',
    contactPhone: opts.contactPhone ?? '',
    deadlineDate: opts.deadlineDate ?? '',
    reference: opts.reference ?? '',
    notes: opts.notes ?? '',
  };
}

function makeSegment(text, opts = {}) {
  return {
    id: uid(),
    text: text ?? '',
    materials: {
      walls: opts.wallsMat ?? 'petg',
      back:  opts.backMat  ?? 'petg',
      front: opts.frontMat ?? 'pmma',
    },
    colors: {
      walls: opts.walls ?? '#ec1d8c',
      back:  opts.back  ?? '#161616',
      front: opts.front ?? '#f5f5f0',
    },
    // letters[]: per-character overrides (null = inherit segment defaults).
    letters: opts.letters ?? [],
    // Detached = treated as a separate sign element (extra spacing in 3D + separate production bundle)
    detached: opts.detached ?? false,
    gapAfterMm: opts.gapAfterMm ?? 30, // extra X-gap (mm) added when detached
  };
}

const defaultSegments = [
  makeSegment('Enseignes', { walls: '#ec1d8c', back: '#161616', front: '#f5f5f0' }),
  makeSegment('3D',        { walls: '#f5f5f0', back: '#161616', front: '#f5f5f0' }),
];

const useEditorStore = create((set, get) => ({
  ...JSON.parse(JSON.stringify(DEFAULT_PROJECT)),
  segments: defaultSegments,
  activeSegmentId: defaultSegments[0].id,

  // ===== Project meta (client, install, notes) =====
  meta: makeMeta({ name: 'Nouveau projet' }),
  setMeta: (patch) => set((s) => ({ meta: { ...s.meta, ...patch } })),

  // ===== Multi-tabs (open projects, like a slicer) =====
  // Each tab = { tabId, projectId|null, meta, snapshot, dirty }
  // snapshot is a deep copy of the persistable state slice.
  tabs: [],          // initialized from a hook below
  activeTabId: null,
  setActiveTabId: (id) => set({ activeTabId: id }),

  svgFile: null,
  orientation: 'iso',
  exploded: false,
  explodeAmount: 1.0,
  showDimensions: true,
  showAnnotations: true,
  clips: { enabled: true, count: 4, type: 'snap' },
  standoffs: { enabled: false, height: 25, diameter: 15, count: 2, material: 'aluminum' },
  background: { type: 'none', color: '#3a3a42', textureScaleMm: 300 }, // textureScaleMm = tile size in mm
  pricing: {
    petgPerKg: 30.0,
    pmmaPerCm2: 0.04,
    woodPerCm2: 0.02,
    pvcPerCm2: 0.03,
    laborPerHour: 35,
    laborHoursPerElementLit: 1.0,
    laborHoursPerElementNoLit: 0.5,
    marginPct: 30,
  },
  printer: { id: 'k2plus', maxX: 350, maxY: 350, maxZ: 350 }, // Creality K2 Plus Combo par défaut
  setPrinter: (printer) => set({ printer }),
  // === Paramètres slicer impression 3D (FDM) ===
  printSettings: {
    nozzleDiameter: 0.4,      // mm
    layerHeight: 0.2,         // mm
    firstLayerHeight: 0.25,   // mm
    wallLineCount: 3,         // perimeters
    topBottomLayers: 4,
    infillPercent: 18,        // %
    infillPattern: 'grid',    // grid | gyroid | triangles | honeycomb
    printSpeed: 60,           // mm/s
    travelSpeed: 120,         // mm/s
    firstLayerSpeed: 25,      // mm/s
    nozzleTemp: 235,          // °C (PETG)
    bedTemp: 80,              // °C (PETG)
    enableSupports: false,
    supportAngle: 50,         // deg
    brimWidth: 0,             // mm
    adhesion: 'brim',         // skirt | brim | raft | none
    retractionEnabled: true,
    retractionDistance: 1.0,  // mm
    retractionSpeed: 40,      // mm/s
    fanSpeed: 50,             // %
    filamentDiameter: 1.75,   // mm
    filamentBrand: 'PolyTerra PETG',
  },
  setPrintSetting: (key, value) =>
    set((s) => ({ printSettings: { ...s.printSettings, [key]: value } })),
  applyPrinterPreset: (presetId) => set((s) => {
    const presets = {
      k2plus: {
        printer: { id: 'k2plus', maxX: 350, maxY: 350, maxZ: 350 },
        printSettings: {
          ...s.printSettings,
          nozzleDiameter: 0.4, layerHeight: 0.2, firstLayerHeight: 0.25,
          wallLineCount: 4, topBottomLayers: 5,
          printSpeed: 200, firstLayerSpeed: 30, travelSpeed: 500,
          nozzleTemp: 240, bedTemp: 80, fanSpeed: 80,
          retractionEnabled: true, retractionDistance: 0.8, retractionSpeed: 40,
          adhesion: 'brim', brimWidth: 5,
          enableSupports: false, supportAngle: 50,
          infillPercent: 20, infillPattern: 'gyroid',
          filamentBrand: 'Creality Hyper PETG',
        },
      },
      k1: {
        printer: { id: 'k1', maxX: 220, maxY: 220, maxZ: 250 },
        printSettings: {
          ...s.printSettings,
          nozzleDiameter: 0.4, layerHeight: 0.2, firstLayerHeight: 0.28,
          wallLineCount: 3, topBottomLayers: 4,
          printSpeed: 180, firstLayerSpeed: 30, travelSpeed: 500,
          nozzleTemp: 240, bedTemp: 80, fanSpeed: 100,
          retractionEnabled: true, retractionDistance: 0.5, retractionSpeed: 40,
          adhesion: 'brim', brimWidth: 5,
          enableSupports: false, supportAngle: 50,
          infillPercent: 20, infillPattern: 'gyroid',
          filamentBrand: 'Creality Hyper PETG',
        },
      },
    };
    return presets[presetId] || {};
  }),
  // Qualité d'impression : « draft / standard / fine / ultra »
  applyQualityPreset: (presetId) => set((s) => {
    const presets = {
      draft:    { layerHeight: 0.28, wallLineCount: 2, topBottomLayers: 3, printSpeed: 250 },
      standard: { layerHeight: 0.20, wallLineCount: 3, topBottomLayers: 4, printSpeed: 200 },
      fine:     { layerHeight: 0.12, wallLineCount: 4, topBottomLayers: 5, printSpeed: 140 },
      ultra:    { layerHeight: 0.08, wallLineCount: 5, topBottomLayers: 6, printSpeed: 90 },
    };
    return { printSettings: { ...s.printSettings, ...(presets[presetId] || {}) } };
  }),
  // === Rim / débord sur socle pour clipsage de la face ===
  socleRim: {
    enabled: false, // disabled by default (user can enable from print settings panel)
    width: 4,
    depth: 2,     // mm — épaisseur du débord (collerette horizontale)
    clearance: 0.3, // mm — jeu pour insertion par clipsage
  },
  setSocleRim: (patch) => set((s) => ({ socleRim: { ...s.socleRim, ...patch } })),
  // === Trous de fixation / passage de câble ===
  mountingHoles: {
    enabled: false,
    pattern: 'corners', // corners | center | manual
    diameter: 6,        // mm
    countersink: false,
    countersinkDiameter: 12,
    countersinkDepth: 2,
  },
  setMountingHoles: (patch) => set((s) => ({ mountingHoles: { ...s.mountingHoles, ...patch } })),
  cableHole: {
    enabled: false, // active pour enseigne lumineuse
    diameter: 12,    // mm — passage du câble alim LED
    position: 'bottom-center', // bottom-center | bottom-left | bottom-right | back-center
  },
  setCableHole: (patch) => set((s) => ({ cableHole: { ...s.cableHole, ...patch } })),
  notification: { message: '', visible: false },
  viewScale: { pxPer100mm: 120, pxPerMm: 1.2 }, // live screen scale (updated by ScaleProbe)
  setViewScale: (patch) => set((s) => ({ viewScale: { ...s.viewScale, ...patch } })),
  zoomRequest: null, // when set, ZoomApplier dollies camera to achieve this real-size %
  setZoomRequest: (pct) => set({ zoomRequest: pct }),
  clearZoomRequest: () => set({ zoomRequest: null }),
  panMode: false, // when true, LEFT click = pan (hand tool)
  togglePanMode: () => set((s) => ({ panMode: !s.panMode })),
  planMode: false, // when true, canvas becomes a white "plan de pose" view with black exploded lines
  togglePlanMode: () => set((s) => ({ planMode: !s.planMode })),
  sidebarOpen: false, // mobile drawer state
  setSidebarOpen: (v) => set({ sidebarOpen: v }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  setInputMode: (inputMode) => set({ inputMode }),
  setFontIndex: (fontIndex) => set({ fontIndex }),
  setStructureType: (structureType) => set({ structureType }),
  setDimension: (key, value) =>
    set((s) => ({ dimensions: { ...s.dimensions, [key]: value } })),
  setLight: (key, value) =>
    set((s) => ({ light: { ...s.light, [key]: value } })),
  setOrientation: (orientation) => set({ orientation }),
  setSvgFile: (svgFile) => set({ svgFile }),
  setExploded: (exploded) => set({ exploded }),
  toggleExploded: () => set((s) => ({ exploded: !s.exploded })),
  togglePartVisibility: (key) =>
    set((s) => ({ partsVisibility: { ...s.partsVisibility, [key]: !s.partsVisibility[key] } })),
  setPartVisibility: (key, v) =>
    set((s) => ({ partsVisibility: { ...s.partsVisibility, [key]: v } })),
  toggleLed: () => set((s) => ({ ledOn: !s.ledOn })),
  setLedDensity: (v) => set({ ledDensity: v }),
  setExplodeAmount: (v) => set({ explodeAmount: v }),
  toggleDimensions: () => set((s) => ({ showDimensions: !s.showDimensions })),
  toggleAnnotations: () => set((s) => ({ showAnnotations: !s.showAnnotations })),
  setClips: (patch) => set((s) => ({ clips: { ...s.clips, ...patch } })),
  setStandoffs: (patch) => set((s) => ({ standoffs: { ...s.standoffs, ...patch } })),
  toggleClips: () => set((s) => ({ clips: { ...s.clips, enabled: !s.clips.enabled } })),
  toggleStandoffs: () => set((s) => ({ standoffs: { ...s.standoffs, enabled: !s.standoffs.enabled } })),
  setPricing: (patch) => set((s) => ({ pricing: { ...s.pricing, ...patch } })),
  setPrinter: (patch) => set((s) => ({ printer: { ...s.printer, ...patch } })),
  setBackground: (patch) => set((s) => ({ background: { ...s.background, ...patch } })),

  setActiveSegment: (id) => set({ activeSegmentId: id }),
  updateSegmentText: (id, text) =>
    set((s) => ({ segments: s.segments.map((seg) => seg.id === id ? { ...seg, text } : seg) })),
  updateSegmentColor: (id, key, value) =>
    set((s) => ({
      segments: s.segments.map((seg) =>
        seg.id === id ? { ...seg, colors: { ...seg.colors, [key]: value } } : seg
      ),
    })),
  updateSegmentMaterial: (id, key, materialId) =>
    set((s) => ({
      segments: s.segments.map((seg) => {
        if (seg.id !== id) return seg;
        // Set default color from new material palette
        const palette = getColorsForMaterial(materialId);
        const newColor = palette[0]?.hex || seg.colors[key];
        return {
          ...seg,
          materials: { ...seg.materials, [key]: materialId },
          colors: { ...seg.colors, [key]: newColor },
        };
      }),
    })),
  addSegment: () => {
    const seg = makeSegment('Texte');
    set((s) => ({ segments: [...s.segments, seg], activeSegmentId: seg.id }));
  },
  toggleSegmentDetached: (id) =>
    set((s) => ({
      segments: s.segments.map((sg) => sg.id === id ? { ...sg, detached: !sg.detached } : sg),
    })),
  setSegmentGap: (id, gapAfterMm) =>
    set((s) => ({
      segments: s.segments.map((sg) => sg.id === id ? { ...sg, gapAfterMm } : sg),
    })),

  // ===== Custom fixation holes (per segment) =====
  // Each hole = { id, xMm, yMm, diameterMm }
  //   xMm = mm from LEFT edge of the segment envelope
  //   yMm = mm from BOTTOM of the segment envelope (= baseline height reference)
  //   diameterMm = per-hole diameter (default 6 mm)
  // When customHoles is non-empty for a segment, it REPLACES the automatic
  // balanced fixation holes on the plan de pose.
  setSegmentCustomHoles: (segId, holes) =>
    set((s) => ({
      segments: s.segments.map((sg) => sg.id === segId ? { ...sg, customHoles: holes } : sg),
    })),
  clearSegmentCustomHoles: (segId) =>
    set((s) => ({
      segments: s.segments.map((sg) => sg.id === segId ? { ...sg, customHoles: [] } : sg),
    })),

  // ===== Per-letter editing (overrides, detach) =====
  selectedLetter: null, // { segmentId, index } or null
  setSelectedLetter: (sel) => set({ selectedLetter: sel }),

  // Set/clear a per-letter override (matKey: 'walls'|'back'|'front')
  setLetterColor: (segId, idx, partKey, hex) =>
    set((s) => ({
      segments: s.segments.map((seg) => {
        if (seg.id !== segId) return seg;
        const letters = [...(seg.letters || [])];
        while (letters.length <= idx) letters.push(null);
        const existing = letters[idx] || {};
        letters[idx] = {
          ...existing,
          colors: { ...(existing.colors || {}), [partKey]: hex },
        };
        return { ...seg, letters };
      }),
    })),
  setLetterMaterial: (segId, idx, partKey, materialId) =>
    set((s) => ({
      segments: s.segments.map((seg) => {
        if (seg.id !== segId) return seg;
        const letters = [...(seg.letters || [])];
        while (letters.length <= idx) letters.push(null);
        const existing = letters[idx] || {};
        const palette = getColorsForMaterial(materialId);
        const newColor = palette[0]?.hex;
        letters[idx] = {
          ...existing,
          materials: { ...(existing.materials || {}), [partKey]: materialId },
          colors: { ...(existing.colors || {}), [partKey]: newColor || existing.colors?.[partKey] },
        };
        return { ...seg, letters };
      }),
    })),
  resetLetterOverride: (segId, idx) =>
    set((s) => ({
      segments: s.segments.map((seg) => {
        if (seg.id !== segId) return seg;
        const letters = [...(seg.letters || [])];
        if (letters[idx]) letters[idx] = null;
        return { ...seg, letters };
      }),
    })),
  // Toggle "detached" flag : pièce physiquement séparée (gap in 3D + exported as separate sign in ZIP)
  toggleLetterDetached: (segId, idx, gapMm = 30) =>
    set((s) => ({
      segments: s.segments.map((seg) => {
        if (seg.id !== segId) return seg;
        const letters = [...(seg.letters || [])];
        while (letters.length <= idx) letters.push(null);
        const existing = letters[idx] || {};
        letters[idx] = { ...existing, detached: !existing.detached, gapMm };
        return { ...seg, letters };
      }),
    })),
  setLetterGap: (segId, idx, gapMm) =>
    set((s) => ({
      segments: s.segments.map((seg) => {
        if (seg.id !== segId) return seg;
        const letters = [...(seg.letters || [])];
        while (letters.length <= idx) letters.push(null);
        const existing = letters[idx] || {};
        letters[idx] = { ...existing, gapMm };
        return { ...seg, letters };
      }),
    })),
  // Split a segment into N segments of 1 character each (✂️ Scinder en lettres)
  splitSegmentIntoLetters: (segId) => set((s) => {
    const idx = s.segments.findIndex((sg) => sg.id === segId);
    if (idx < 0) return {};
    const seg = s.segments[idx];
    const chars = (seg.text || '').split('');
    if (chars.length < 2) return {};
    const newSegs = chars.map((ch, i) => {
      const ov = seg.letters?.[i] || {};
      return makeSegment(ch, {
        wallsMat: ov.materials?.walls ?? seg.materials.walls,
        backMat:  ov.materials?.back  ?? seg.materials.back,
        frontMat: ov.materials?.front ?? seg.materials.front,
        walls: ov.colors?.walls ?? seg.colors.walls,
        back:  ov.colors?.back  ?? seg.colors.back,
        front: ov.colors?.front ?? seg.colors.front,
      });
    });
    const next = [...s.segments.slice(0, idx), ...newSegs, ...s.segments.slice(idx + 1)];
    return { segments: next, activeSegmentId: newSegs[0].id };
  }),
  removeSegment: (id) => set((s) => {
    if (s.segments.length <= 1) return {};
    const segments = s.segments.filter((seg) => seg.id !== id);
    return {
      segments,
      activeSegmentId: s.activeSegmentId === id ? segments[0].id : s.activeSegmentId,
    };
  }),
  moveSegment: (id, dir) => set((s) => {
    const idx = s.segments.findIndex((seg) => seg.id === id);
    if (idx < 0) return {};
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= s.segments.length) return {};
    const segments = s.segments.slice();
    [segments[idx], segments[newIdx]] = [segments[newIdx], segments[idx]];
    return { segments };
  }),

  showNotification: (message) => {
    set({ notification: { message, visible: true } });
    setTimeout(() => set({ notification: { message: '', visible: false } }), 3500);
  },
  reset: () => set({
    ...JSON.parse(JSON.stringify(DEFAULT_PROJECT)),
    segments: [
      makeSegment('Enseignes', { walls: '#ec1d8c', back: '#161616', front: '#f5f5f0' }),
      makeSegment('3D', { walls: '#f5f5f0', back: '#161616', front: '#f5f5f0' }),
    ],
    activeSegmentId: null,
    svgFile: null, exploded: false, orientation: 'iso',
  }),
}));

export default useEditorStore;
